import {
  createContext,
  useContext,
  type CSSProperties,
  type FC,
  type ReactNode,
} from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { HolderOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  workOrderDraggableId,
  workOrderDroppableId,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import type { KanbanActiveDragKind } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/WorkOrderKanbanColumn/WorkOrderKanbanColumn';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';

export function shouldMountWorkOrderDraggable(
  activeDragKind: KanbanActiveDragKind,
  activeDragId: string | null,
  workOrderId: string,
  canDrag: boolean,
): boolean {
  if (!canDrag) return false;
  if (activeDragKind === 'ticket') return false;
  if (activeDragKind === 'workOrder') return activeDragId === workOrderId;
  return true;
}

export function shouldMountWorkOrderDroppable(
  activeDragKind: KanbanActiveDragKind,
  canManage: boolean,
): boolean {
  return canManage && activeDragKind === 'ticket';
}

export function shouldMountTicketDraggable(
  activeDragKind: KanbanActiveDragKind,
  activeDragId: string | null,
  ticketId: string,
  disabled: boolean,
): boolean {
  if (disabled) return false;
  if (activeDragKind === 'workOrder') return false;
  if (activeDragKind === 'ticket') return activeDragId === ticketId;
  return true;
}

type WoDragHandleApi = {
  attributes: ReturnType<typeof useDraggable>['attributes'];
  listeners: ReturnType<typeof useDraggable>['listeners'];
};

const WoDragHandleContext = createContext<WoDragHandleApi | null>(null);

function fadeStyle(isDragging: boolean): CSSProperties | undefined {
  return isDragging ? { opacity: 0.35, pointerEvents: 'none' } : undefined;
}

type DragShellProps = {
  workOrderId: string;
  workOrderCode: string;
  /** Stable element tree — must not be created via render-prop each move. */
  children: ReactNode;
};

/**
 * Owns `useDraggable`. Re-renders on pointer moves, but `children` keep their
 * element identity from the parent so memoized card chrome does not.
 */
export const WorkOrderDraggableShell: FC<DragShellProps> = ({
  workOrderId,
  workOrderCode,
  children,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: workOrderDraggableId(workOrderId),
    data: {
      type: 'workOrder',
      workOrderId,
      workOrderCode,
    },
  });

  return (
    <WoDragHandleContext.Provider value={{ attributes, listeners }}>
      <div ref={setNodeRef} style={fadeStyle(isDragging)}>
        {children}
      </div>
    </WoDragHandleContext.Provider>
  );
};

/** Shared grip-handle look — icon centered in the hit target. */
const woDragHandleButtonStyle: CSSProperties = {
  flexShrink: 0,
  boxSizing: 'border-box',
  border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
  background: '#f1f5f9',
  color: '#64748b',
  borderRadius: 6,
  width: 22,
  height: 36,
  padding: 0,
  margin: 0,
  cursor: 'grab',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  lineHeight: 0,
  appearance: 'none',
  WebkitAppearance: 'none',
};

/** Cheap consumer — the only subtree that should update on drag moves. */
export const WorkOrderCardDragHandle: FC = () => {
  const api = useContext(WoDragHandleContext);
  const { t } = useTranslation('maintenance');
  const label = t('workOrders.kanban.drag.workOrderHandle');

  if (!api) {
    return (
      <span
        aria-hidden
        style={{
          ...woDragHandleButtonStyle,
          opacity: 0.4,
          cursor: 'default',
        }}
      />
    );
  }

  return (
    <Tooltip title={label}>
      <button
        type="button"
        aria-label={label}
        onClick={(event) => event.stopPropagation()}
        style={woDragHandleButtonStyle}
        {...api.listeners}
        {...api.attributes}
      >
        <HolderOutlined
          style={{
            fontSize: 12,
            lineHeight: 0,
            display: 'block',
            margin: 0,
            verticalAlign: 0,
          }}
        />
      </button>
    </Tooltip>
  );
};

type DropShellProps = {
  workOrderId: string;
  status: string;
  children: ReactNode;
};

/**
 * Owns `useDroppable`. Highlights the wrapper on isOver; children stay stable.
 */
export const WorkOrderDroppableShell: FC<DropShellProps> = ({
  workOrderId,
  status,
  children,
}) => {
  const { t } = useTranslation('maintenance');
  const { setNodeRef, isOver } = useDroppable({
    id: workOrderDroppableId(workOrderId),
    data: {
      type: 'workOrderDrop',
      workOrderId,
      status,
    },
  });

  return (
    <div ref={setNodeRef} style={{ position: 'relative' }}>
      <div
        style={
          isOver
            ? {
                borderRadius: 12,
                boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.15)',
              }
            : undefined
        }
      >
        {children}
      </div>
      {isOver ? (
        <div
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            padding: '8px 10px',
            borderRadius: 8,
            background: '#eff6ff',
            color: WORK_ORDER_UI.suggestionLink,
            border: `2px dashed ${WORK_ORDER_UI.suggestionLink}`,
            fontSize: 12,
            fontWeight: 600,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          {t('workOrders.kanban.drag.dropTicketHere')}
        </div>
      ) : null}
    </div>
  );
};
